import { z } from 'zod';
import { TimestampSchema } from './common.js';
import { UserRefSchema } from './domain.js';
/**
 * Заметки направления — единственное содержимое Jisy, которого нет в Jira: договорённости,
 * ссылки, скриншоты из переписки.
 *
 * Тело хранится деревом ProseMirror, потому что редактор на клиенте работает именно с ним.
 * Разбирать документ схемой всё равно нужно: в базу он приходит от браузера, а `jsonb` примет
 * что угодно, включая узел с «src» на чужой сайт или разметку в тысячу уровней вложенности.
 */
/** Максимальная вложенность документа: защита от дерева, на котором рекурсия съест стек. */
const MAX_DEPTH = 20;
export const EditorNodeSchema = z.lazy(() => z.object({
    type: z.string().min(1).max(40),
    attrs: z.record(z.string(), z.unknown()).optional(),
    content: z.array(EditorNodeSchema).max(2000).optional(),
    marks: z
        .array(z.object({
        type: z.string().min(1).max(40),
        attrs: z.record(z.string(), z.unknown()).optional(),
    }))
        .max(20)
        .optional(),
    text: z.string().optional(),
}));
function depthOf(node, level = 1) {
    if (!node.content || node.content.length === 0)
        return level;
    return Math.max(...node.content.map((child) => depthOf(child, level + 1)));
}
export const NoteBodySchema = EditorNodeSchema.refine((node) => node.type === 'doc' && depthOf(node) <= MAX_DEPTH, 'документ заметки должен быть деревом doc допустимой глубины');
/** Строка списка заметок: тело в неё не входит — списки открываются часто, документы тяжёлые. */
export const NoteSummarySchema = z.object({
    id: z.string(),
    projectId: z.string(),
    title: z.string(),
    /** Первая строка текста без разметки — подпись под заголовком в списке. */
    excerpt: z.string(),
    updatedAt: TimestampSchema,
    updatedBy: UserRefSchema.nullable(),
});
export const NoteSchema = NoteSummarySchema.extend({
    body: NoteBodySchema,
    createdAt: TimestampSchema,
});
export const NoteListSchema = z.object({ notes: z.array(NoteSummarySchema) });
export const NOTE_TITLE_MAX = 200;
export const NoteCreateSchema = z.object({
    title: z.string().trim().max(NOTE_TITLE_MAX).default(''),
    body: NoteBodySchema.optional(),
});
/**
 * Правка заметки. Заголовок и тело приходят порознь: заголовок правится отдельным полем,
 * а тело — автосохранением редактора, и слать документ ради переименования незачем.
 */
export const NotePatchSchema = z
    .object({
    title: z.string().trim().max(NOTE_TITLE_MAX).optional(),
    body: NoteBodySchema.optional(),
})
    .refine((value) => value.title !== undefined || value.body !== undefined, 'пустая правка');
/** Потолок картинки. Тот же лимит стоит и в схеме базы, и на разборе тела запроса. */
export const NOTE_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
export const NOTE_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
export const NoteImageSchema = z.object({
    id: z.string(),
    /** Адрес, который редактор ставит в узел картинки. */
    url: z.string(),
    contentType: z.enum(NOTE_IMAGE_TYPES),
    byteSize: z.number().int().positive(),
});
/**
 * Текст документа без разметки — для поиска и для подписи в списке.
 * Живёт в общем пакете: сервер кладёт его в колонку, клиент теми же правилами показывает начало.
 */
export function noteToPlainText(body) {
    const parts = [];
    const walk = (node) => {
        if (typeof node.text === 'string')
            parts.push(node.text);
        // Разрыв абзаца — это пробел: иначе соседние абзацы слипаются в одно слово.
        if (node.content) {
            for (const child of node.content)
                walk(child);
            parts.push(' ');
        }
    };
    walk(body);
    return parts.join('').replace(/\s+/gu, ' ').trim();
}
/** Заголовок нетронутой заметки берётся из первой строки текста — как в блокнотах. */
export function noteTitleOrFallback(title, plainText, fallback) {
    if (title.trim().length > 0)
        return title.trim();
    const firstLine = plainText.trim().split(' ').slice(0, 8).join(' ');
    return firstLine.length > 0 ? firstLine : fallback;
}
//# sourceMappingURL=notes.js.map